import {useEffect, useState, useRef, useCallback, useMemo, memo} from "react";
import {useSelector} from "react-redux";
import {isEmpty} from "es-toolkit/compat";
import {config} from "@/config";
import {selectBiomarkersData} from "@/features/biomarkers/biomarkersSelectors";
import {selectPartnersShowHcReports} from "@/features/partners/partnersSelectors";
import {
  selectResultsData,
  selectResultsHealthCategories,
  selectResultsHistory,
  selectResultsMetadata,
  selectResultsStatus,
  selectResultsSupplements,
} from "@/features/results/resultsSelectors";

const isForth = config.websiteId === 1;
const isEdge = config.websiteId === 2;
const isBiomarkerTracking = config.websiteId === 3;

export const useResults = (options = {}) => {
  const {errorMsg = "", hasFailed = false, isFetching = false} = useSelector(selectResultsStatus);

  const data = useSelector(selectResultsData);
  const metadata = useSelector(selectResultsMetadata);
  const history = useSelector(selectResultsHistory);
  const healthCategories = useSelector(selectResultsHealthCategories);
  const supplements = [];
  // const supplements = useSelector(selectResultsSupplements)?.filter(
  //   supplement => !supplement.name.toLowerCase().includes("vitamin d"),
  // );
  const biomarkerInformation = useSelector(selectBiomarkersData);
  const showB2BHcReports = useSelector(selectPartnersShowHcReports);

  const latestResult = useMemo(() => {
    return [...data]?.sort((a, b) => new Date(b.orderCompleteDate) - new Date(a.orderCompleteDate))?.[0] || {};
  }, [data]);

  const filteredResult = useMemo(() => {
    const resultFound = data?.find(result => result.orderId == options.orderId) || {};
    const doctorReview = resultFound?.doctorReview || {};

    return {
      ...resultFound,
      customerComment: doctorReview?.customerComment || "",
      hideCustomerComment: doctorReview?.hideCustomerComment || true,
      professionalProfile: doctorReview?.professionalProfile || "",
    };
  }, [data, options.orderId]);

  const biomarkersResult = useMemo(() => {
    if (options.orderId) return filteredResult?.biomarkers || [];
    return latestResult?.biomarkers || [];
  }, [filteredResult, latestResult, options.orderId]);

  const [modalBiomarkerDetails, setModalBiomarkerDetails] = useState({});
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [modalResultInfo, setModalResultInfo] = useState({});

  const healthCategoryExist = useRef();
  const healthComponentExist = useRef();
  const biomarkersNoTested = useRef([]);

  const handleCleanBiomarkerDetails = useCallback(() => {
    setModalBiomarkerDetails({});
  }, []);

  const handleModalBiomarkerDetails = useCallback(
    biomarkerResultId => {
      if (["", "0", 0, null].includes(biomarkerResultId)) {
        setModalBiomarkerDetails({});
        return;
      }

      if (modalBiomarkerDetails?.biomarkerResultId != biomarkerResultId) {
        setIsModalLoading(true);

        const biomarkerId = biomarkersResult.find(
          biomarker => biomarker.biomarkerResultId == biomarkerResultId,
        )?.biomarkerId;

        const markerDetails =
          biomarkersResult.find(biomarker => biomarker.biomarkerResultId == biomarkerResultId) || {};
        const markerInformation = biomarkerInformation?.find(biomarker => biomarker.biomarkerId == biomarkerId) || {};
        const details = {
          ...markerDetails,
          ...markerInformation,
        };

        setModalBiomarkerDetails(details);
        setIsModalLoading(false);
      }
    },
    [biomarkersResult, biomarkerInformation],
  );

  const handleModalResultInfo = useCallback(
    biomarkerResultId => {
      if (modalResultInfo?.biomarkerResultId != biomarkerResultId) {
        const [marker] = biomarkersResult.filter(biomarker => biomarker.biomarkerResultId == biomarkerResultId);

        setModalResultInfo({...marker});
      }
    },
    [biomarkersResult, modalResultInfo],
  );

  const healthCategoryDetails = useMemo(() => {
    const healthScore = filteredResult.healthScores || [];
    const healthCategory = healthScore.find(category => category.categoryId == options.healthCategoryId) || {};

    if (isEmpty(healthCategory)) {
      healthCategoryExist.current = false;
      return {};
    }

    healthCategoryExist.current = true;
    return healthCategory;
  }, [filteredResult, options.healthCategoryId]);

  const getHealthCategoryDetails = useCallback(
    async ({orderId, healthCategoryId}) => {
      if (!orderId || !healthCategoryId) {
        return;
      }

      const healthScore = data.filter(result => result.orderId == orderId)[0].healthScores || [];
      const healthCategory = healthScore.filter(category => category.categoryId == healthCategoryId)?.[0] || {};

      if (isEmpty(healthCategory)) {
        return {};
      }

      return healthCategory;
    },
    [data],
  );

  /**
   * Get health categories history
   *
   * The index of the array returned is the health category Id
   * @return Object of objects
   */

  const healthCategoryHistory = useMemo(() => {
    const hasBiomarkerTrackingAccess = isBiomarkerTracking && showB2BHcReports;

    if (!isForth && !hasBiomarkerTrackingAccess) return {};
    const newHealthCategoryHistory = {};

    if (data?.length > 0) {
      data?.forEach(order => {
        const {orderCompleteDate, orderId, healthScores = []} = order;
        healthScores?.forEach(hc => {
          const {categoryId} = hc;
          const newHealthCoachValue = {
            ...hc,
            orderCompleteDate,
            orderId,
          };

          if (newHealthCategoryHistory[categoryId]) {
            newHealthCategoryHistory[categoryId] = [...newHealthCategoryHistory[categoryId], newHealthCoachValue];
          } else {
            newHealthCategoryHistory[categoryId] = [newHealthCoachValue];
          }
        });
      });
    }

    return newHealthCategoryHistory;
  }, [data, showB2BHcReports]);

  const getHealthCategoryHistory = useCallback(async () => {
    const hasBiomarkerTrackingAccess = isBiomarkerTracking && showB2BHcReports;

    if (!isForth && !hasBiomarkerTrackingAccess) return {};
    const newHealthCategoryHistory = {};

    data?.forEach(order => {
      const {orderCompleteDate, orderId, healthScores} = order;
      healthScores?.forEach(hc => {
        const {categoryId} = hc;
        const newHealthCoachValue = {
          ...hc,
          orderCompleteDate,
          orderId,
        };

        if (newHealthCategoryHistory[categoryId]) {
          newHealthCategoryHistory[categoryId] = [...newHealthCategoryHistory[categoryId], newHealthCoachValue];
        } else {
          newHealthCategoryHistory[categoryId] = [newHealthCoachValue];
        }
      });
    });

    return newHealthCategoryHistory;
  }, [data, showB2BHcReports]);

  const healthComponentDetails = useMemo(() => {
    const hasBiomarkerTrackingAccess = isBiomarkerTracking && showB2BHcReports;

    if (!isForth && !hasBiomarkerTrackingAccess) return {};

    const healthScore = filteredResult.healthScores || [];
    const healthCategory = healthScore.find(category => category.categoryId == options.healthCategoryId) || {};
    const healthComponent =
      healthCategory?.components?.find(component => component.componentResultId == options.healthComponentId) || {};

    if (isEmpty(healthComponent)) {
      healthComponentExist.current = false;
      return {};
    }

    healthComponentExist.current = true;
    return healthComponent;
  }, [data, showB2BHcReports, filteredResult.healthScores, options.healthCategoryId, options.healthComponentId]);

  const getHealthComponentDetails = useCallback(
    async ({orderId, healthCategoryId, healthComponentId}) => {
      const healthScore = data.filter(result => result.orderId == orderId)[0].healthScores || [];
      const healthCategory = healthScore.find(category => category.categoryId == healthCategoryId) || {};
      const healthComponent =
        healthCategory?.components?.find(component => component.componentResultId == healthComponentId) || {};

      if (isEmpty(healthComponent)) {
        return {};
      }

      return healthComponent;
    },
    [data],
  );

  /**
   * Get health component history of a health category
   *
   * The index of the array returned is the health component Id
   * @return Object of objects
   */

  const healthComponentHistory = useMemo(() => {
    const hasBiomarkerTrackingAccess = isBiomarkerTracking && showB2BHcReports;

    if (!isForth && !hasBiomarkerTrackingAccess) return {};
    const newHealthComponentsHistory = {};

    data?.forEach(order => {
      const {healthScores, orderCompleteDate, orderId} = order;

      healthScores?.forEach(hc => {
        const {categoryId, categoryName, components} = hc;

        components?.forEach(component => {
          const {componentId} = component;

          const newHealthComponent = {
            ...component,
            // eslint-disable-next-line camelcase
            scored_out_of: component.score_max,
            orderCompleteDate,
            orderId,
            categoryId,
            categoryName,
          };

          if (newHealthComponentsHistory[componentId]) {
            newHealthComponentsHistory[componentId] = [...newHealthComponentsHistory[componentId], newHealthComponent];
          } else {
            newHealthComponentsHistory[componentId] = [newHealthComponent];
          }
        });
      });
    });

    return newHealthComponentsHistory;
  }, [data, showB2BHcReports]);

  const getHealthComponentHistory = useCallback(async () => {
    const hasBiomarkerTrackingAccess = isBiomarkerTracking && showB2BHcReports;

    if (!isForth && !hasBiomarkerTrackingAccess) return {};
    const newHealthComponentsHistory = {};

    data?.forEach(order => {
      const {healthScores, orderCompleteDate, orderId} = order;

      healthScores?.forEach(hc => {
        const {categoryId, categoryName, components} = hc;

        components?.forEach(component => {
          const {componentId} = component;

          const newHealthComponent = {
            ...component,
            // eslint-disable-next-line camelcase
            scored_out_of: component.score_max,
            orderCompleteDate,
            orderId,
            categoryId,
            categoryName,
          };

          if (newHealthComponentsHistory[componentId]) {
            newHealthComponentsHistory[componentId] = [...newHealthComponentsHistory[componentId], newHealthComponent];
          } else {
            newHealthComponentsHistory[componentId] = [newHealthComponent];
          }
        });
      });
    });

    return newHealthComponentsHistory;
  }, [data, showB2BHcReports]);

  const checkBiomarkerHasBeenTested = useCallback(
    biomarkerId => {
      if (!biomarkerId) return;

      return data.some(result => result.biomarkers.some(marker => marker.biomarkerId === biomarkerId));
    },
    [data],
  );

  const totalAllMarkers = useMemo(
    () => [...new Set([...(healthCategories || [])].flatMap(category => category.biomarkerIds))]?.length,
    [healthCategories],
  );

  const totalAllMarkersTested = useMemo(() => {
    const allMarkers = [...new Set([...(healthCategories || [])].flatMap(category => category.biomarkerIds))] || [];

    let total = 0;

    allMarkers?.forEach((biomarkerId, i) => {
      const hasBeenTested = checkBiomarkerHasBeenTested(biomarkerId);
      if (hasBeenTested) {
        total += 1;
      }

      if (!hasBeenTested) {
        biomarkersNoTested.current.push(biomarkerId);
      }

      // * This console log return the biomarker name and if it's tested
      // console.log(
      //   biomarkerInformation.information.filter(biomarker => biomarker.biomarkerId == biomarkerId)?.[0].biomarkerName,
      //   hasBeenTested,
      // );
    });

    return total;
  }, [healthCategories, checkBiomarkerHasBeenTested]);

  const checkOrderIdExist = useCallback(() => {
    return data.some(result => result.orderId == options?.orderId);
  }, [data]);

  const getHealthCategoriesMarkers = useCallback(
    healthCategoryId => {
      const category = healthCategories?.find(category => category.id == healthCategoryId) || {};
      const {biomarkerIds = []} = category;

      return [...new Set(biomarkerIds)];
    },
    [healthCategories],
  );

  const getResultByOrderId = useCallback(
    ({orderId}) => {
      const [result] = data.filter(result => result.orderId == orderId) || [];

      return result;
    },
    [data],
  );

  const countBiomarkersTested = useCallback(
    healthCategoryId => {
      if (!healthCategoryId) return;
      const biomarkerIds = getHealthCategoriesMarkers(healthCategoryId) || [];

      let total = 0;
      biomarkerIds?.forEach(biomarkerId => checkBiomarkerHasBeenTested(biomarkerId) && total++);

      return total;
    },
    [getHealthCategoriesMarkers, checkBiomarkerHasBeenTested],
  );

  return {
    allResults: data,
    metadata,
    history,
    latestResult,
    biomarkersResult,
    modalBiomarkerDetails,
    isModalLoading,
    modalResultInfo,
    filteredResult,
    healthCategories,
    supplements,
    totalAllMarkers,
    totalAllMarkersTested,
    biomarkersNoTested: [...new Set(biomarkersNoTested.current)],
    healthCategoryDetails,
    healthCategoryExist: healthCategoryExist.current,
    healthComponentDetails,
    healthComponentExist: healthComponentExist.current,
    healthCategoryHistory,
    healthComponentHistory,
    getResultByOrderId,
    handleModalBiomarkerDetails,
    handleCleanBiomarkerDetails,
    handleModalResultInfo,
    checkOrderIdExist,
    checkBiomarkerHasBeenTested,
    getHealthCategoriesMarkers,
    countBiomarkersTested,
    isLoading: false,
    isFetching,
    hasFailed,
    errorMsg,
  };
};
